import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Typography } from '../../interfaces/typography';
import { useFixedDropdown } from './useFixedDropdown';

interface Option {
  value: string;
  label: string;
}

interface FixedSingleSelectProps {
  options: Option[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
}

export function FixedSingleSelect({ options, value, onChange, placeholder = 'Select an option...' }: FixedSingleSelectProps) {
  const [search, setSearch] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(search.toLowerCase())
  );

  const {
    isOpen,
    setIsOpen,
    dropdownPosition,
    containerRef,
    triggerRef,
    dropdownRef,
    handleToggle
  } = useFixedDropdown(filteredOptions.length);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      const option = filteredOptions[focusedIndex];
      onChange(option.value);
      setIsOpen(false);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleTriggerClick = () => {
    handleToggle();
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const selectedOption = options.find(option => option.value === value);

  return (
    <>
      <div className="relative w-full" ref={containerRef}>
        <div
          ref={triggerRef}
          className="border rounded-lg bg-white px-2 py-1.5 h-[38px] cursor-pointer flex items-center"
          onClick={handleTriggerClick}
        >
          <Typography 
            variant="mediumParagraph" 
            color={selectedOption ? '#111827' : '#6B7280'} 
            className="truncate flex-1"
          >
            {selectedOption ? selectedOption.label : placeholder}
          </Typography>
          <ChevronDown size={18} className="shrink-0 text-gray-400 ml-2" />
        </div>
      </div>

      {/* Fixed positioned dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="fixed bg-white border rounded-lg shadow-lg flex flex-col"
          style={{
            top: dropdownPosition.top + 'px',
            left: dropdownPosition.left + 'px',
            width: dropdownPosition.width + 'px',
            maxHeight: dropdownPosition.maxHeight + 'px',
            zIndex: 2147483647,
            position: 'fixed',
            isolation: 'isolate'
          }}
        >
          {/* Search header - fixed height */}
          <div className="p-1.5 border-b flex-shrink-0">
            <div className="relative">
              <Search size={18} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                className="w-full pl-7 pr-2 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Search options..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  fontSize: '13px',
                  fontFamily: 'Calibri,Arial,Helvetica,sans-serif'
                }}
              />
            </div>
          </div>
          
          {/* Options list - scrollable */}
          <div className="flex-1 overflow-y-auto">
            {filteredOptions.map((option, index) => (
              <div
                key={option.value}
                className={`px-2 py-1.5 cursor-pointer flex items-center gap-2 ${
                  focusedIndex === index ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
              >
                <div className={`shrink-0 w-4 h-4 border rounded-full flex items-center justify-center ${
                  value === option.value ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                }`}>
                  {value === option.value && <Check size={12} className="text-white" />}
                </div>
                <Typography variant="mediumParagraph" color="#111827" className="truncate">
                  {option.label}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
} 